import { useEffect, useState } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { loginUser, registerUser } from '../utils/apiHelpers';
import { useAuthStore, authResponseToState } from '../store/useAuthStore';
import { useToast } from '../context/ToastContext';
import { parseAuthError, validateRegistrationForm } from '../utils/errorHandler';

type AuthModalProps = {
  open: boolean;
  onClose: () => void;
};

type StatusState =
  | { type: 'success'; message: string }
  | { type: 'error'; message: string }
  | null;

const initialLoginState = {
  identifier: '',
  password: '',
};

const initialRegisterState = {
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone: '',
  address: '',
};

export default function AuthModal({ open, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [loginData, setLoginData] = useState(initialLoginState);
  const [registerData, setRegisterData] = useState(initialRegisterState);
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});
  const [registerErrors, setRegisterErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<StatusState>(null);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setAuth = useAuthStore((state) => state.setAuth);
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setLoginErrors({});
    setRegisterErrors({});
  }, [open, mode]);

  const closeModal = () => {
    setStatus(null);
    setLoginErrors({});
    setRegisterErrors({});
    setShowLoginPassword(false);
    setShowRegisterPassword(false);
    onClose();
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginErrors({});
    setStatus(null);

    // Defensive: never call .trim() directly on a value that might be
    // undefined/null. String(...) + `?? ''` guarantees a string first.
    const identifier = String(loginData?.identifier ?? '').trim();
    const rawPassword = String(loginData?.password ?? '');
    const password = rawPassword;
    const errors: Record<string, string> = {};

    if (!identifier) {
      errors.identifier = 'Email or username is required';
    }

    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (Object.keys(errors).length > 0) {
      setLoginErrors(errors);
      setStatus({ type: 'error', message: 'Please correct the errors below.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await loginUser({ identifier, password });
      setAuth(authResponseToState(response));

      showToast('Logged in successfully!', 'success');
      setStatus({ type: 'success', message: 'Welcome back! You are now signed in.' });
      setLoginData(initialLoginState);
      setTimeout(() => {
        closeModal();
      }, 700);
    } catch (error) {
      const parsedError = parseAuthError(error);
      setLoginErrors(parsedError.fieldErrors || {});
      setStatus({ type: 'error', message: parsedError.message });
      showToast(parsedError.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRegisterErrors({});
    setStatus(null);

    const validation = validateRegistrationForm({
      firstName: registerData?.firstName ?? '',
      lastName: registerData?.lastName ?? '',
      username: registerData?.username ?? '',
      email: registerData?.email ?? '',
      password: registerData?.password ?? '',
      confirmPassword: registerData?.confirmPassword ?? '',
    });

    if (!validation.isValid) {
      setRegisterErrors(validation.errors);
      setStatus({ type: 'error', message: 'Please correct the errors below.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await registerUser({
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        username: registerData.username,
        email: registerData.email,
        password: registerData.password,
        phone: registerData.phone || undefined,
        address: registerData.address || undefined,
      });

      setAuth({
        user: {
          id: response?.user?.id,
          username: response?.user?.username ?? '',
          email: response?.user?.email ?? '',
          role: response?.user?.role ?? 'customer',
          profile_pic: response?.user?.profile_pic ?? null,
          phone: String(response?.user?.phone ?? ''),
          address: String(response?.user?.address ?? ''),
        },
        token: response?.token,
        refreshToken: response?.refresh,
      });

      showToast('Account created successfully! You are now signed in.', 'success');
      setStatus({ type: 'success', message: 'Your account has been created and logged in.' });
      setRegisterData(initialRegisterState);
      setTimeout(() => {
        setMode('login');
        closeModal();
      }, 700);
    } catch (error) {
      const parsedError = parseAuthError(error);
      setRegisterErrors(parsedError.fieldErrors || {});
      setStatus({ type: 'error', message: parsedError.message });
      showToast(parsedError.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto px-4 py-6 sm:px-6">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal} />
      <div className="relative z-10 w-full max-w-4xl rounded-[32px] bg-white shadow-[0_40px_80px_rgba(0,0,0,0.12)] ring-1 ring-black/5">
        <div className="flex flex-col gap-4 px-6 py-6 sm:px-8 sm:py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.24em] text-gray-500">Account access</p>
              <h2 className="mt-2 text-3xl font-semibold text-gray-900">
                {mode === 'login' ? 'Sign in to your account' : 'Create a new account'}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                {mode === 'login'
                  ? 'Use your email or username to sign in securely.'
                  : 'Register once and enjoy faster checkout, order tracking, and saved favorites.'}
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
              aria-label="Close auth modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
            <aside className="rounded-[28px] bg-[var(--primary-red)] p-6 text-white shadow-sm">
              <div className="space-y-5">
                <div>
                  <p className="text-sm uppercase tracking-[0.24em] text-orange-100">Welcome</p>
                  <h3 className="mt-3 text-xl font-semibold">Quick access</h3>
                </div>
                <div className="space-y-4 text-sm leading-relaxed text-orange-50">
                  <p>Login or register to manage orders, wishlist, and checkout faster.</p>
                  <p>Your credentials are sent securely to the server and handled safely.</p>
                </div>
                <div className="flex flex-col gap-2 rounded-3xl bg-white/10 p-4 text-sm text-orange-100">
                  <p className="font-semibold">Benefits</p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li>Track orders</li>
                    <li>Save favorites</li>
                    <li>Faster checkout</li>
                  </ul>
                </div>
              </div>
            </aside>

            <div className="space-y-5">
              <div className="flex flex-wrap gap-2 rounded-3xl bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className={`rounded-3xl px-5 py-2 text-sm font-semibold transition ${
                    mode === 'login'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-pressed={mode === 'login'}
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className={`rounded-3xl px-5 py-2 text-sm font-semibold transition ${
                    mode === 'register'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  aria-pressed={mode === 'register'}
                >
                  Sign Up
                </button>
              </div>

              {status && (
                <div
                  className={`rounded-3xl border px-4 py-3 text-sm ${
                    status.type === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-rose-200 bg-rose-50 text-rose-800'
                  }`}
                >
                  {status.message}
                </div>
              )}

              {mode === 'login' ? (
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="auth-identifier" className="mb-2 block text-sm font-semibold text-gray-700">
                      Email or username
                    </label>
                    <input
                      id="auth-identifier"
                      type="text"
                      value={loginData.identifier}
                      onChange={(event) => setLoginData((prev) => ({ ...prev, identifier: event.target.value }))}
                      className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${
                        loginErrors.identifier ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                      }`}
                      placeholder="Username or email"
                    />
                    {loginErrors.identifier && (
                      <p className="mt-2 text-sm text-rose-600">{loginErrors.identifier}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="auth-password" className="mb-2 block text-sm font-semibold text-gray-700">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="auth-password"
                        type={showLoginPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))}
                        className={`w-full rounded-3xl border px-4 py-3 pr-12 text-sm outline-none transition ${
                          loginErrors.password ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                        }`}
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-3 inline-flex items-center rounded-full p-1 text-gray-500 transition hover:text-gray-900"
                        aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                      >
                        {showLoginPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {loginErrors.password && (
                      <p className="mt-2 text-sm text-rose-600">{loginErrors.password}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center rounded-3xl bg-[var(--primary-red)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[var(--primary-red-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleRegisterSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="auth-first-name" className="mb-2 block text-sm font-semibold text-gray-700">
                        First name
                      </label>
                      <input
                        id="auth-first-name"
                        type="text"
                        value={registerData.firstName}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, firstName: event.target.value }))}
                        className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${
                          registerErrors.firstName ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                        }`}
                        placeholder="First name"
                      />
                      {registerErrors.firstName && (
                        <p className="mt-2 text-sm text-rose-600">{registerErrors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="auth-last-name" className="mb-2 block text-sm font-semibold text-gray-700">
                        Last name
                      </label>
                      <input
                        id="auth-last-name"
                        type="text"
                        value={registerData.lastName}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, lastName: event.target.value }))}
                        className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${
                          registerErrors.lastName ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                        }`}
                        placeholder="Last name"
                      />
                      {registerErrors.lastName && (
                        <p className="mt-2 text-sm text-rose-600">{registerErrors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="auth-username" className="mb-2 block text-sm font-semibold text-gray-700">
                      Username
                    </label>
                    <input
                      id="auth-username"
                      type="text"
                      value={registerData.username}
                      onChange={(event) => setRegisterData((prev) => ({ ...prev, username: event.target.value }))}
                      className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${
                        registerErrors.username ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                      }`}
                      placeholder="Username"
                    />
                    {registerErrors.username && (
                      <p className="mt-2 text-sm text-rose-600">{registerErrors.username}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="auth-email" className="mb-2 block text-sm font-semibold text-gray-700">
                      Email address
                    </label>
                    <input
                      id="auth-email"
                      type="email"
                      value={registerData.email}
                      onChange={(event) => setRegisterData((prev) => ({ ...prev, email: event.target.value }))}
                      className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${
                        registerErrors.email ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                      }`}
                      placeholder="Email"
                    />
                    {registerErrors.email && (
                      <p className="mt-2 text-sm text-rose-600">{registerErrors.email}</p>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="auth-password-register" className="mb-2 block text-sm font-semibold text-gray-700">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="auth-password-register"
                          type={showRegisterPassword ? 'text' : 'password'}
                          value={registerData.password}
                          onChange={(event) => setRegisterData((prev) => ({ ...prev, password: event.target.value }))}
                          className={`w-full rounded-3xl border px-4 py-3 pr-12 text-sm outline-none transition ${
                            registerErrors.password ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                          }`}
                          placeholder="Create a password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-3 inline-flex items-center rounded-full p-1 text-gray-500 transition hover:text-gray-900"
                          aria-label={showRegisterPassword ? 'Hide password' : 'Show password'}
                        >
                          {showRegisterPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {registerErrors.password && (
                        <p className="mt-2 text-sm text-rose-600">{registerErrors.password}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="auth-confirm-password" className="mb-2 block text-sm font-semibold text-gray-700">
                        Confirm password
                      </label>
                      <input
                        id="auth-confirm-password"
                        type={showRegisterPassword ? 'text' : 'password'}
                        value={registerData.confirmPassword}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                        className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${
                          registerErrors.confirmPassword ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-gray-200 bg-white text-gray-900'
                        }`}
                        placeholder="Confirm password"
                      />
                      {registerErrors.confirmPassword && (
                        <p className="mt-2 text-sm text-rose-600">{registerErrors.confirmPassword}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="auth-phone" className="mb-2 block text-sm font-semibold text-gray-700">
                        Phone number
                      </label>
                      <input
                        id="auth-phone"
                        type="tel"
                        value={registerData.phone}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, phone: event.target.value }))}
                        className="w-full rounded-3xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition text-gray-900"
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="auth-address" className="mb-2 block text-sm font-semibold text-gray-700">
                        Address
                      </label>
                      <input
                        id="auth-address"
                        type="text"
                        value={registerData.address}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, address: event.target.value }))}
                        className="w-full rounded-3xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition text-gray-900"
                        placeholder="City, street, etc."
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center rounded-3xl bg-[var(--primary-red)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[var(--primary-red-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Creating account...' : 'Create account'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
